import {
  ApiErrorResponse,
  Beacon,
  BeaconTableSorting,
  Organization,
  OrganizationMutableProperties,
  PagedResponse,
  UpdateResult,
  User,
} from "./models";
import {
  ApiCall,
  ApiState,
  PagedApiState,
  useApiState,
  useAuthorizedFetch,
  useGetAuthorized,
  usePagedApiState,
} from "./useApiState";

export function useOrganizations(
  abortFetch?: boolean
): PagedApiState<Organization> {
  return usePagedApiState(
    useGetAuthorized<PagedResponse<Organization>>(
      "organizations",
      undefined,
      undefined,
      abortFetch
    )
  );
}

export function useOrganization(
  organizationId: string
): ApiState<Organization | undefined> {
  return useApiState(
    useGetAuthorized<Organization>(`organizations/${organizationId}`),
    undefined
  );
}

export function useMyUser(userId: string): ApiState<User | undefined> {
  return useApiState(useGetAuthorized<User>(`users/${userId}`), undefined);
}

export function useCreateOrganization(): ApiCall<
  OrganizationMutableProperties,
  Organization
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (organization: OrganizationMutableProperties) => {
    const result = await authorizedFetch("organizations", {
      method: "post",
      body: JSON.stringify(organization),
    });
    return (await result.json()) as Organization;
  };
}

export function useUpdateOrganization(
  organizationId: string
): ApiCall<OrganizationMutableProperties, Organization> {
  const authorizedFetch = useAuthorizedFetch();
  return async (organization: OrganizationMutableProperties) => {
    const result = await authorizedFetch(`organizations/${organizationId}`, {
      method: "put",
      body: JSON.stringify(organization),
    });
    return (await result.json()) as Organization;
  };
}

export function useDeleteOrganization(
  organizationId: string
): ApiCall<void, void> {
  const authorizedFetch = useAuthorizedFetch();
  return async () => {
    const result = await authorizedFetch(`organizations/${organizationId}`, {
      method: "delete",
    });
    if (!result.ok) {
      throw result.status;
    }
  };
}

export function useOrganizationUsers(
  organizationId: string
): PagedApiState<User> {
  return usePagedApiState(
    useGetAuthorized<PagedResponse<User>>(
      `organizations/${organizationId}/users`
    )
  );
}

export function useUsers(
  assignedToOrganization?: boolean
): PagedApiState<User> {
  const query =
    assignedToOrganization === undefined
      ? ""
      : `?assignedToOrganization=${assignedToOrganization}`;
  return usePagedApiState(
    useGetAuthorized<PagedResponse<User>>("users" + query)
  );
}

export function useCreateUser(): ApiCall<
  { email: string; orgId: string, consumerId: string },
  UpdateResult
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (user: { email: string; orgId: string, consumerId: string }) => {
    const result = await authorizedFetch("users", {
      method: "post",
      body: JSON.stringify(user),
    });
    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "User created successfully" },
        },
      };
    }
  };
}

export function useAssignUsersToOrganization(): ApiCall<
  { userIds: string[]; organizationId: string },
  void
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (a: { userIds: string[]; organizationId: string }) => {
    const result = await authorizedFetch(
      `organizations/${a.organizationId}/users`,
      { method: "put", body: JSON.stringify(a.userIds) }
    );
    if (!result.ok) {
      throw result.status;
    }
  };
}

export function useUnassignUsersFromOrganization(): ApiCall<
  { userIds: string[]; organizationId: string },
  void
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (a: { userIds: string[]; organizationId: string }) => {
    const result = await authorizedFetch(
      `organizations/${a.organizationId}/users`,
      { method: "delete", body: JSON.stringify(a.userIds) }
    );
    if (!result.ok) {
      throw result.status;
    }
  };
}

export function useOrganizationBeacons(
  organizationId: string,
  sorting?: BeaconTableSorting
): PagedApiState<Beacon> {
  let url = `organizations/${organizationId}/beacons`;

  if (sorting?.sortOrderId !== undefined) {
    url += "?sort_order=" + sorting.sortOrderId;
  }
  return usePagedApiState(useGetAuthorized<PagedResponse<Beacon>>(url));
}

export function useAssignBeaconsToOrganization(): ApiCall<
  { beaconIds: string[]; organizationId: string },
  void
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (a: { beaconIds: string[]; organizationId: string }) => {
    const result = await authorizedFetch(
      `organizations/${a.organizationId}/beacons`,
      { method: "put", body: JSON.stringify(a.beaconIds) }
    );
    if (!result.ok) {
      throw result.status;
    }
  };
}

export function useUnassignBeaconsFromOrganization(): ApiCall<
  { beaconIds: string[]; organizationId: string },
  void
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (a: { beaconIds: string[]; organizationId: string }) => {
    const result = await authorizedFetch(
      `organizations/${a.organizationId}/beacons`,
      { method: "delete", body: JSON.stringify(a.beaconIds) }
    );
    if (!result.ok) {
      throw result.status;
    }
  };
}
